import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Typography } from '@mui/material';
import { ArrowIcon, ChatIcon } from '@sharedCR/coreDesignSystem/icons';
import {
  TeamChatChannel,
  TeamChatChannelList,
  TeamChatConnector,
} from 'app/shared/components/TeamChat';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { TeamChatChannelType } from 'constants/teamChat';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getUuid } from 'store/auth/selectors';
import { setInternalChatActiveChannelAction } from 'store/system/actions';
import { selectInternalChatActiveChannel } from 'store/system/selectors';
import { fetchChannelsAction } from 'store/teamChat/actions';
import {
  selectTeamChatChannelsByType,
  selectTeamChatIsLoadingChannels,
} from 'store/teamChat/selectors';

import {
  channelHeaderNameStyles,
  channelHeaderStyles,
  containerStyles,
  emptyStateStyles,
} from './InternalChat.styles';
import { InternalChatTestIds } from './InternalChat.testIds';

const selectInternalChannels = selectTeamChatChannelsByType(TeamChatChannelType.INTERNAL);

const InternalChat = () => {
  const dispatch = useDispatch();

  const loggedUserId = useSelector(getUuid);
  const activeChannelUrl = useSelector(selectInternalChatActiveChannel);
  const channels = useSelector(selectInternalChannels);
  const isLoadingChannels = useSelector(selectTeamChatIsLoadingChannels);

  const onSelectChannel = useCallback(
    chatId => {
      dispatch(setInternalChatActiveChannelAction(chatId ?? null));
    },
    [dispatch],
  );

  const onBackToList = useCallback(() => {
    dispatch(setInternalChatActiveChannelAction(null));
  }, [dispatch]);

  useEffectSafeStrictMode(() => {
    dispatch(fetchChannelsAction());
  }, [dispatch]);

  if (!loggedUserId) {
    return (
      <Box sx={emptyStateStyles}>
        <ChatIcon size={IconSize[48]} />
        <Typography variant="regular14">Internal chat unavailable</Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <TeamChatConnector>
        {!activeChannelUrl ? (
          <TeamChatChannelList
            activeChannelId={activeChannelUrl}
            channels={channels}
            isLoading={isLoadingChannels}
            onSelectChannel={onSelectChannel}
          />
        ) : (
          <>
            <Box sx={channelHeaderStyles}>
              <IconButton
                data-testid={InternalChatTestIds.BACK}
                size="small"
                onClick={onBackToList}
              >
                <ArrowIcon direction={IconDirection.LEFT} size={IconSize[20]} />
              </IconButton>
              <Typography sx={channelHeaderNameStyles} variant="bold14">
                Internal Chat
              </Typography>
            </Box>
            <TeamChatChannel
              isHeaderHidden
              chatId={activeChannelUrl}
              currentUserId={loggedUserId}
              key={activeChannelUrl}
            />
          </>
        )}
      </TeamChatConnector>
    </Box>
  );
};

export default memo(InternalChat);
